"use client";

import { useCallback, useEffect, useRef, type RefObject } from "react";
import type React from "react";

/** Options for {@link useContentEditableField}. Edit-mode state is owned by the caller so this hook
 *  stays headless and reusable across builders with different state stores. */
export interface UseContentEditableFieldOptions {
  /** Whether the field is currently in edit mode (owned by the caller). */
  isEditing: boolean;
  /** Committed value — seeded into the element when edit mode begins, and restored on cancel. */
  value: string;
  /** Called with the trimmed, length-clamped text when the edit commits (blur / Enter). */
  onCommit: (text: string) => void;
  /** Called when the edit is cancelled (Escape). The element's text is restored first. */
  onCancel: () => void;
  /** Called to enter edit mode (click / Enter / Space) when idle and not disabled. */
  onRequestEdit: () => void;
  /** Optional max length — pasted and committed text is clamped to it so an oversized paste can't
   *  silently exceed a server field limit and wedge autosave. */
  maxLength?: number;
  /** When true the field is read-only: no edit affordances are wired up. */
  disabled?: boolean;
}

/** Props to spread onto the editable element (assign {@link UseContentEditableFieldResult.ref} separately). */
export interface ContentEditableFieldProps {
  contentEditable: boolean;
  suppressContentEditableWarning: boolean;
  role: "textbox" | "button" | undefined;
  tabIndex: number | undefined;
  onClick: (event: React.MouseEvent) => void;
  onBlur: () => void;
  onKeyDown: (event: React.KeyboardEvent) => void;
  onPaste: (event: React.ClipboardEvent) => void;
}

export interface UseContentEditableFieldResult {
  ref: RefObject<HTMLElement>;
  fieldProps: ContentEditableFieldProps;
}

/** Collapses the selection to the end of `el`'s content — used after seeding and after an over-limit paste. */
function moveCaretToEnd(el: HTMLElement): void {
  const range = document.createRange();
  const selection = window.getSelection();
  range.selectNodeContents(el);
  range.collapse(false);
  selection?.removeAllRanges();
  selection?.addRange(range);
}

/** Inserts `text` as a literal text node at the current caret position, replacing any selection.
 *  Deliberately not `document.execCommand("insertText", ...)`: Chrome's implementation splits a `\n`
 *  into a new `<div>` instead of a real newline character, and that boundary produces NO `\n` in
 *  `.textContent` at all — the line break is visible while editing but silently lost on commit. */
function insertTextAtCaret(text: string): void {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return;
  const range = selection.getRangeAt(0);
  range.deleteContents();
  const node = document.createTextNode(text);
  range.insertNode(node);
  range.setStartAfter(node);
  range.collapse(true);
  selection.removeAllRanges();
  selection.addRange(range);
}

/**
 * Headless mechanics for an inline, click-to-edit plain-text field backed by a `contentEditable`
 * element. Owns the DOM interaction only — focus/caret placement on edit-enter, commit on blur/Enter,
 * restore-and-cancel on Escape, plain-text paste (never HTML), keyboard entry (Enter/Space), and
 * length clamping — while the caller owns whether the field is editing and what commit does.
 *
 * Commits read `textContent` (never `innerHTML`), so pasted markup can never enter a plain-string
 * field. This is the shared primitive behind both the email builder's inline editing and Landing
 * Page v2's `InlineTextEdit`.
 *
 * @param options - Edit state, current value, and commit/cancel/request-edit callbacks
 * @returns A `ref` for the editable element and `fieldProps` to spread onto it
 */
export function useContentEditableField(options: UseContentEditableFieldOptions): UseContentEditableFieldResult {
  const { isEditing, value, onCommit, onCancel, onRequestEdit, maxLength, disabled } = options;

  const ref = useRef<HTMLElement>(null);
  // The value captured when edit mode began — restored verbatim if the user cancels with Escape.
  const originalValueRef = useRef(value);

  const clamp = useCallback((text: string) => (maxLength ? text.slice(0, maxLength) : text), [maxLength]);

  const commit = useCallback(() => {
    if (!ref.current) return;
    onCommit(clamp((ref.current.textContent ?? "").trim()));
  }, [onCommit, clamp]);

  // Seed the element, focus it, and drop the caret at the end when edit mode begins.
  useEffect(() => {
    if (!isEditing || !ref.current) return;
    originalValueRef.current = value;
    ref.current.textContent = value;
    ref.current.focus();
    moveCaretToEnd(ref.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs on edit-mode transitions only; seeding on every `value` change would fight the user's typing
  }, [isEditing]);

  const handleClick = useCallback(
    (event: React.MouseEvent) => {
      if (disabled || isEditing) return;
      event.stopPropagation();
      onRequestEdit();
    },
    [disabled, isEditing, onRequestEdit]
  );

  const handleBlur = useCallback(() => {
    if (!isEditing) return;
    commit();
  }, [isEditing, commit]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (isEditing) {
        if (event.key === "Enter") {
          event.preventDefault();
          commit();
        } else if (event.key === "Escape") {
          event.preventDefault();
          if (ref.current) ref.current.textContent = originalValueRef.current;
          onCancel();
        }
        return;
      }
      if (disabled) return;
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onRequestEdit();
      }
    },
    [isEditing, disabled, commit, onCancel, onRequestEdit]
  );

  const handlePaste = useCallback(
    (event: React.ClipboardEvent) => {
      if (!isEditing) return;
      // Strip all formatting — plain-string fields must never absorb pasted markup.
      event.preventDefault();
      const text = event.clipboardData.getData("text/plain");
      insertTextAtCaret(text);
      // Enforce the field cap even across a large paste, so it can't silently exceed the server limit.
      if (maxLength && ref.current && (ref.current.textContent?.length ?? 0) > maxLength) {
        ref.current.textContent = clamp(ref.current.textContent ?? "");
        moveCaretToEnd(ref.current);
      }
    },
    [isEditing, maxLength, clamp]
  );

  return {
    ref,
    fieldProps: {
      contentEditable: isEditing,
      suppressContentEditableWarning: true,
      role: isEditing ? "textbox" : disabled ? undefined : "button",
      tabIndex: isEditing || disabled ? undefined : 0,
      onClick: handleClick,
      onBlur: handleBlur,
      onKeyDown: handleKeyDown,
      onPaste: handlePaste,
    },
  };
}
